import {
  Box,
  DeleteIconButton,
  DELETE_WARNING,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  newToast,
  Stack,
  Tooltip,
  Typography,
} from "@compass/core";
import ArrowDropDownCircleIcon from '@mui/icons-material/ArrowDropDownCircle';
import { ReportPropertyBase, UserExportDisplayFieldsSnapshot } from "generated/openApi";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { setAppToast } from "redux/reducers/appSlice";
import ApiService from "services/apiService";
import { DisplaySnapshotMenuCreate } from "./DisplaySnapshotMenuCreate";

export const DisplaySnapshotMenu = (props: {
  displayFields: string[];
  setDisplayFields: (displayFields: string[]) => void;
  displayProperties: ReportPropertyBase[];
  onSelectedDisplayFields: (displayFields: string[]) => void;
  exportTypeId?: number;
  exportSubTypeId?: number;
}) => {
  const dispatch = useDispatch();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [snapshots, setSnapshots] = useState<UserExportDisplayFieldsSnapshot[]>();
  const [isCreating, setIsCreating] = useState<boolean>(false);

  useEffect(() => {
    fetch();
  }, []);

  const fetch = () => {
    ApiService.exports.getDisplayFieldsSnapshots(props.exportTypeId, props.exportSubTypeId).then((response) => {
      setSnapshots(response);
    });
  };

  const handleSnapshotClick = (snapshot: UserExportDisplayFieldsSnapshot) => {
    const displayFields = snapshot.displayFields?.map((x) => x.name);
    props.onSelectedDisplayFields(displayFields || []);
    setAnchorEl(null);
  };

  const postDelete = (id: number) => {
    if (window.confirm(DELETE_WARNING)) {
      ApiService.exports.deleteDisplayFieldsSnapshot(id).then(() => {
        fetch();
        dispatch(setAppToast(newToast("success")));
      });
    }
  };

  const selectAll = () => {
    props.setDisplayFields(props.displayProperties.map((x) => x.identifier));
  };

  const clearAll = () => {
    props.setDisplayFields([]);
  };
  const width = "250px";

  return (
    <>
      <IconButton onClick={(e) => setAnchorEl(e.currentTarget)} size="large">
        <ArrowDropDownCircleIcon color="info"/>
      </IconButton>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
        <MenuItem
          sx={{ minWidth: width }}
          onClick={() => {
            selectAll();
            setAnchorEl(null);
          }}
          disabled={props.displayProperties.length === props.displayFields.length}
        >
          + Select All
        </MenuItem>

        <MenuItem
          sx={{ minWidth: width }}
          onClick={() => {
            clearAll();
            setAnchorEl(null);
          }}
          disabled={props.displayFields.length === 0}
        >
          - Clear All
        </MenuItem>

        <Divider />

        {snapshots?.map((snapshot) => (
          <MenuItem sx={{ minWidth: width }} onClick={() => handleSnapshotClick(snapshot)}>
            <Stack direction="row" justifyContent={"space-between"} alignItems="center" width="100%" spacing="lg">
              <Box>
                <Box>{snapshot.name}</Box>
                <Typography variant="caption">{snapshot.displayFields?.length} selected fields</Typography>
              </Box>

              <DeleteIconButton
                onClick={(e) => {
                  e.stopPropagation();
                  postDelete(snapshot.id);
                }}
              />
            </Stack>
          </MenuItem>
        ))}

        {snapshots && snapshots?.length > 0 && <Divider />}

        <Tooltip
          title={
            <Stack spacing="sm">
              <Box>
                Saving a snapshot will remember your selected fields preference into a profile, which can be used later
                to quickly select fields.
              </Box>
              {!props.displayFields ||
                (props.displayFields.length === 0 && (
                  <Box>You cannot add a new snapshot now because no fields are selected</Box>
                ))}
            </Stack>
          }
        >
          <MenuItem
            sx={{ minWidth: width }}
            onClick={() => setIsCreating(true)}
            disabled={!props.displayFields || props.displayFields.length === 0}
          >
            + Add New Snapshot
          </MenuItem>
        </Tooltip>
      </Menu>

      {isCreating && (
        <DisplaySnapshotMenuCreate
          displayFields={props.displayFields}
          exportTypeId={props.exportTypeId}
          exportSubTypeId={props.exportSubTypeId}
          onClose={() => {
            setIsCreating(false);
          }}
          onSave={() => {
            fetch();
          }}
        />
      )}
    </>
  );
};
